import React, { useState, useEffect, useRef, Suspense } from 'react';
import { fetchDisasterNews } from './services/geminiService';
import { sendNotification } from './services/notificationService';
import { requestForToken, onMessageListener } from './services/firebaseConfig';
import { GroundingLink, AppTab } from './types';
import Dashboard from './components/Dashboard';
import Navbar from './components/Navbar';
import Logo from './components/Logo';
import ErrorBoundary from './components/ErrorBoundary';
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";

// Lazy Load Components
const ChatAssistant = React.lazy(() => import('./components/ChatAssistant'));
const EmergencyContacts = React.lazy(() => import('./components/EmergencyContacts'));
const SurvivalToolkit = React.lazy(() => import('./components/SurvivalToolkit'));
const Support = React.lazy(() => import('./components/Support'));
const SupportModal = React.lazy(() => import('./components/SupportModal'));
const FeedbackModal = React.lazy(() => import('./components/FeedbackModal'));
const ChangelogModal = React.lazy(() => import('./components/ChangelogModal'));

const PageLoader = () => (
  <div className="flex flex-col items-center justify-center min-h-[50vh] space-y-4">
    <div className="w-10 h-10 border-4 border-red-500 border-t-transparent rounded-full animate-spin"></div>
    <p className="text-xs font-bold text-gray-400 uppercase tracking-widest animate-pulse">Memuat Modul...</p>
  </div>
);

const AppContent: React.FC = () => {
  const [activeTab, setActiveTab] = useState<AppTab>('dashboard');
  const [news, setNews] = useState<{ text: string; links: GroundingLink[]; isCached?: boolean; isStale?: boolean; updatedAt?: number }>({ text: '', links: [] });
  const [loadingNews, setLoadingNews] = useState(true); // Default loading true agar sinkron
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showChangelogModal, setShowChangelogModal] = useState(false);
  const [changelogData, setChangelogData] = useState<any[]>([]);
  const [hasUpdate, setHasUpdate] = useState(false);
  const [appVersion, setAppVersion] = useState<string>('v1.0.0');
  const prevNewsRef = useRef<string>('');

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const data = await fetchDisasterNews("Kota Tegal dan Kabupaten Tegal");
        setNews(data);
      } finally {
        setLoadingNews(false);
        
        // Modal logic after data is ready to prevent blank background
        const hasSeenModal = localStorage.getItem('has_seen_support_v1');
        if (!hasSeenModal) {
          setTimeout(() => {
            setShowSupportModal(true);
          }, 2000);
        }
      }
    };
    
    loadInitialData();

    // Check for updates
    const checkUpdates = async () => {
      try {
        const response = await fetch('/changelog.json');
        const data = await response.json();
        setChangelogData(data.history);
        setAppVersion(`v${data.latestVersion}`);

        const savedVersion = localStorage.getItem('app_version');
        const latestVersion = data.latestVersion;

        if (savedVersion !== latestVersion) {
          setHasUpdate(true);
        }
      } catch (error) {
        console.error('Failed to check updates:', error);
      }
    };
    checkUpdates();
    
    // Check saved notification preference
    const savedNotif = localStorage.getItem('tegal_notifications');
    if (savedNotif === 'true' && Notification.permission === 'granted') {
      setNotificationsEnabled(true);
      requestForToken();
    }

    // Listen for foreground messages
    onMessageListener()
      .then((payload: any) => {
        console.log('Foreground message received:', payload);
        const { title, body } = payload.notification;
        sendNotification(title, { body, icon: '/images/logo.png' });
      })
      .catch((err) => console.log('failed: ', err));

  }, []);

  const handleCloseSupportModal = () => {
    setShowSupportModal(false);
    localStorage.setItem('has_seen_support_v1', 'true');
  };

  const handleCloseChangelogModal = () => {
    setShowChangelogModal(false);
    if (changelogData.length > 0) {
      localStorage.setItem('app_version', changelogData[0].version);
      setHasUpdate(false);
    }
  };

  const toggleNotifications = async () => {
    if (!notificationsEnabled) {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        setNotificationsEnabled(true);
        localStorage.setItem('tegal_notifications', 'true');
        
        // Request FCM Token
        requestForToken();

        // Send welcome notification
        sendNotification('Siaga Tegal: Notifikasi Aktif', {
          body: 'Anda akan menerima peringatan dini cuaca ekstrem & kabar penting seputar Tegal.',
          icon: '/icon-192x192.png',
          badge: '/icon-192x192.png'
        });
      } else {
        alert('Izin notifikasi ditolak oleh browser.');
      }
    } else {
      setNotificationsEnabled(false);
      localStorage.setItem('tegal_notifications', 'false');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col antialiased">
      <Analytics />
      <SpeedInsights />
      {/* Support Modal mounted at top level */}
      <Suspense fallback={<PageLoader />}>
        <SupportModal isOpen={showSupportModal} onClose={handleCloseSupportModal} />
        <FeedbackModal isOpen={showFeedbackModal} onClose={() => setShowFeedbackModal(false)} appVersion={appVersion} />
        <ChangelogModal isOpen={showChangelogModal} onClose={handleCloseChangelogModal} changelog={changelogData} />
      </Suspense>
      
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-100 p-4 sticky top-0 z-[50]">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          <Logo showText size={36} />
          
          <div className="flex items-center gap-3">
            <div className="hidden xs:flex items-center gap-2 bg-green-50 px-3 py-1.5 rounded-xl text-[10px] font-black text-green-700 uppercase tracking-widest border border-green-100">
              <span className="animate-pulse w-1.5 h-1.5 bg-green-500 rounded-full"></span>
              Sistem Aktif
            </div>
            <button 
              onClick={toggleNotifications}
              className={`w-10 h-10 flex items-center justify-center rounded-xl transition-all active:scale-90 ${
                notificationsEnabled ? 'bg-red-50 text-red-600 shadow-inner' : 'bg-gray-100 text-gray-400'
              }`}
            >
              <span className="text-lg">{notificationsEnabled ? '🔔' : '🔕'}</span>
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-4xl mx-auto w-full p-4 pb-28">
        <Suspense fallback={<div className="flex justify-center p-8">Loading...</div>}>
          {activeTab === 'dashboard' && (
            <Dashboard 
              news={news} 
              loading={loadingNews} 
              onRefresh={() => {
                setLoadingNews(true);
                fetchDisasterNews("Kota Tegal dan Kabupaten Tegal", true).then((data) => {
                  setNews(data);
                  setLoadingNews(false);
                });
              }}
              notificationsEnabled={notificationsEnabled}
              toggleNotifications={toggleNotifications}
            />
          )}
          {activeTab === 'chat' && <ChatAssistant />}
          {activeTab === 'toolkit' && <SurvivalToolkit />}
          {activeTab === 'contacts' && <EmergencyContacts />}
          {activeTab === 'info' && (
            <Support 
              onOpenFeedback={() => setShowFeedbackModal(true)} 
              onOpenChangelog={() => setShowChangelogModal(true)}
              hasUpdate={hasUpdate}
              appVersion={appVersion}
            />
          )}
        </Suspense>

        <footer className="mt-8 text-center pb-4">
          <a 
            href="https://open-meteo.com/" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="text-[10px] font-medium text-gray-400 hover:text-blue-600 transition-colors"
          >
            Weather Data Powered by Open-Meteo.com
          </a>
        </footer>
      </main>

      <Navbar activeTab={activeTab} setActiveTab={setActiveTab} hasUpdate={hasUpdate} />
    </div>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <AppContent />
    </ErrorBoundary>
  );
};

export default App;